import { Fragment, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { useTableSettings } from '@/hooks/useTableSettings';
import type { PageSize } from '@/hooks/useTableSettings';
import { distinctValues, nodeToText, textMatches } from '@/lib/tableFilter';

export type { PageSize };

export interface Column<T> {
  /** Stabile Spalten-ID, auch localStorage- und testid-Schlüssel. */
  id: string;
  /** Kopfzelleninhalt. */
  header: ReactNode;
  /** Zellinhalt einer Zeile. */
  cell: (row: T) => ReactNode;
  /**
   * Text, auf dem gefiltert wird. Default: `nodeToText(cell(row))`.
   * Für Zellen, deren sichtbarer Text nicht zum Filtern taugt
   * (Icons, Datumsformate, Badges mit Kürzeln), hier setzen.
   */
  filterValue?: ((row: T) => string) | undefined;
  /**
   * 'text' (Default) = Eingabefeld "enthaelt".
   * 'select' = Auswahlfeld mit den distinkten Werten der Spalte.
   * false = keine Filterzelle für diese Spalte.
   */
  filter?: 'text' | 'select' | false | undefined;
  /** Klassen für die Datenzelle (TD). */
  className?: string | undefined;
  /** Klassen für die Kopfzelle (TH). */
  headerClassName?: string | undefined;
  /** aria-label-Teil, wenn `header` kein reiner String ist. */
  filterLabel?: string | undefined;
}

/**
 * Serverseitig geblätterte Liste: die Tabelle blättert nicht selbst,
 * sondern meldet Seitenwechsel und Seitengröße nach oben. Spaltenfilter
 * wirken dann nur auf die bereits geladenen Zeilen.
 */
export interface DataTableServer {
  pageSize: number;
  /** Default: [10, 25, 50, 100, 200, 500]. */
  pageSizeOptions?: readonly number[] | undefined;
  onPageSizeChange: (size: number) => void;
  hasPrev: boolean;
  onPrev: () => void;
  hasNext: boolean;
  onNext: () => void;
  /** Freitext links in der Fußzeile, z.B. "~855 Eintraege gesamt". */
  totalLabel?: string | undefined;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T, index: number) => string;
  /** sr-only caption der Tabelle. Pflicht (Barrierefreiheit). */
  caption: string;
  /** Filterzeile abschaltbar. Default true. */
  filters?: boolean | undefined;
  /** localStorage-Schlüssel, z.B. 'systems'. Ohne ihn keine Persistenz. */
  storageKey?: string | undefined;
  /** Auch die Filtertexte merken. Default false. */
  persistFilters?: boolean | undefined;
  /** Clientseitige Standard-Seitengröße. Default 50. */
  defaultPageSize?: PageSize | undefined;
  /** Clientseitige Stufen. Default [10, 25, 50, 100, 200, 'alle']. */
  pageSizeOptions?: readonly PageSize[] | undefined;
  /** Gesetzt = Servermodus. */
  server?: DataTableServer | undefined;
  onRowClick?: ((row: T) => void) | undefined;
  rowSelected?: ((row: T) => boolean) | undefined;
  rowMuted?: ((row: T) => boolean) | undefined;
  rowTestId?: ((row: T) => string) | undefined;
  /** Klassen für den äußeren Table-Container. */
  className?: string | undefined;
  /** Inhalt statt Tabelle, wenn `rows` leer ist. */
  emptyContent?: ReactNode;
  /** Text, wenn Filter alles wegfiltern. Default siehe unten. */
  noMatchContent?: ReactNode;
  /** Zusätzliche Zeilen ganz unten im tbody (z.B. Summenzeile). */
  footerRows?: ReactNode;
  /** Zusatzknöpfe rechts in der Kopfleiste über der Tabelle. */
  toolbar?: ReactNode;
  /**
   * Aufklappbare Zusatzzeile unter einer Datenzeile (Fehlerdetails,
   * Zuordnungsvorschläge). Liefert die Funktion etwas anderes als
   * null/undefined/false, rendert die Tabelle darunter eine volle Zeile mit
   * passendem colSpan. Der Inhalt zählt nicht für die Filterung.
   */
  rowExtra?: ((row: T) => ReactNode) | undefined;
}

const DEFAULT_CLIENT_PAGE_SIZES: readonly PageSize[] = [10, 25, 50, 100, 200, 'alle'];
const DEFAULT_SERVER_PAGE_SIZES: readonly number[] = [10, 25, 50, 100, 200, 500];

const FILTER_CONTROL_CLASS =
  'w-full rounded border border-border bg-surface px-2 py-1 text-xs font-normal text-text placeholder:text-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary';

const PAGE_SIZE_SELECT_CLASS =
  'rounded border border-border bg-surface px-2 py-1 text-sm text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary';

const TEXT_FILTER_DEBOUNCE_MS = 150;

function filterLabelOf<T>(col: Column<T>): string {
  if (col.filterLabel !== undefined) {
    return col.filterLabel;
  }
  if (typeof col.header === 'string') {
    return col.header;
  }
  return col.id;
}

function cellText<T>(col: Column<T>, row: T): string {
  return col.filterValue ? col.filterValue(row) : nodeToText(col.cell(row));
}

function hasActiveFilterValues(filters: Record<string, string>): boolean {
  return Object.values(filters).some((value) => value.trim() !== '');
}

function smallestNumericSize(options: readonly PageSize[]): number | undefined {
  let min: number | undefined;
  for (const opt of options) {
    if (typeof opt === 'number' && Number.isFinite(opt) && opt > 0) {
      if (min === undefined || opt < min) {
        min = opt;
      }
    }
  }
  return min;
}

function prependCurrentPageSize(current: number, options: readonly number[]): number[] {
  if (options.includes(current)) {
    return [...options];
  }
  return [current, ...options];
}

function TextFilterCell({
  columnId,
  label,
  value,
  setFilter,
}: {
  columnId: string;
  label: string;
  value: string;
  setFilter: (columnId: string, value: string) => void;
}): JSX.Element {
  const [local, setLocal] = useState(value);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    if (local === value) {
      return;
    }
    const handle = window.setTimeout(() => {
      setFilter(columnId, local);
    }, TEXT_FILTER_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [local, value, columnId, setFilter]);

  return (
    <input
      type="text"
      className={FILTER_CONTROL_CLASS}
      placeholder="Filter …"
      aria-label={`Filter ${label}`}
      data-testid={`datatable-filter-${columnId}`}
      value={local}
      onChange={(event) => setLocal(event.target.value)}
    />
  );
}

function SelectFilterCell({
  columnId,
  label,
  value,
  options,
  setFilter,
}: {
  columnId: string;
  label: string;
  value: string;
  options: readonly string[];
  setFilter: (columnId: string, value: string) => void;
}): JSX.Element {
  return (
    <select
      className={FILTER_CONTROL_CLASS}
      aria-label={`Filter ${label}`}
      data-testid={`datatable-filter-${columnId}`}
      value={value}
      onChange={(event) => setFilter(columnId, event.target.value)}
    >
      <option value="">Alle</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const {
    columns,
    rows,
    rowKey,
    caption,
    filters: filtersEnabled = true,
    storageKey,
    persistFilters,
    defaultPageSize,
    pageSizeOptions,
    server,
    onRowClick,
    rowSelected,
    rowMuted,
    rowTestId,
    className,
    emptyContent,
    noMatchContent,
    footerRows,
    toolbar,
    rowExtra,
  } = props;

  const pageSizeId = useId();
  const {
    pageSize: clientPageSize,
    setPageSize: setClientPageSize,
    filters,
    setFilter,
    resetFilters,
  } = useTableSettings({
    storageKey,
    defaultPageSize: defaultPageSize ?? 50,
    persistFilters,
  });

  const [page, setPage] = useState(0);

  const cellValues = useMemo(() => {
    return rows.map((row) => columns.map((col) => cellText(col, row)));
  }, [rows, columns]);

  const filteredIndexes = useMemo(() => {
    const active: Array<{ colIndex: number; needle: string; isSelect: boolean }> = [];
    columns.forEach((col, colIndex) => {
      const needle = filters[col.id];
      if (needle === undefined || needle.trim() === '') {
        return;
      }
      active.push({ colIndex, needle, isSelect: col.filter === 'select' });
    });
    if (active.length === 0) {
      return rows.map((_, index) => index);
    }
    const matched: number[] = [];
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const rowVals = cellValues[rowIndex];
      if (!rowVals) {
        continue;
      }
      const ok = active.every((item) => {
        const cellVal = rowVals[item.colIndex] ?? '';
        if (item.isSelect) {
          return cellVal.trim() === item.needle.trim();
        }
        return textMatches(cellVal, item.needle);
      });
      if (ok) {
        matched.push(rowIndex);
      }
    }
    return matched;
  }, [rows, columns, filters, cellValues]);

  const selectOptionsByColumn = useMemo(() => {
    const map = new Map<string, string[]>();
    columns.forEach((col, colIndex) => {
      if (col.filter !== 'select') {
        return;
      }
      const values = cellValues.map((rowVals) => rowVals[colIndex] ?? '');
      map.set(col.id, distinctValues(values));
    });
    return map;
  }, [columns, cellValues]);

  const filteredCount = filteredIndexes.length;
  const totalCount = rows.length;
  const activeFilters = hasActiveFilterValues(filters);
  const clientOptions = pageSizeOptions ?? DEFAULT_CLIENT_PAGE_SIZES;
  const minNumeric = smallestNumericSize(clientOptions);
  const totalPages =
    clientPageSize === 'alle' || clientPageSize <= 0
      ? 1
      : Math.max(1, Math.ceil(filteredCount / clientPageSize));
  const safePage = page >= totalPages ? 0 : page;

  const displayedIndexes = useMemo(() => {
    if (server !== undefined || clientPageSize === 'alle' || clientPageSize <= 0) {
      return filteredIndexes;
    }
    const start = safePage * clientPageSize;
    return filteredIndexes.slice(start, start + clientPageSize);
  }, [server, clientPageSize, filteredIndexes, safePage]);

  useEffect(() => {
    setPage(0);
  }, [filters, clientPageSize]);

  useEffect(() => {
    if (page >= totalPages) {
      setPage(0);
    }
  }, [page, totalPages]);

  if (columns.length === 0 || rows.length === 0) {
    return <>{emptyContent}</>;
  }

  const showFilterRow = filtersEnabled && columns.some((col) => col.filter !== false);
  const showClientPager = server === undefined && minNumeric !== undefined && rows.length > minNumeric;
  const countLabel =
    !activeFilters && filteredCount === totalCount
      ? `${totalCount} Zeilen`
      : `${filteredCount} von ${totalCount} Zeilen`;
  const showServerHint = server !== undefined && activeFilters;

  const serverPageSizeOptions = server
    ? prependCurrentPageSize(server.pageSize, server.pageSizeOptions ?? DEFAULT_SERVER_PAGE_SIZES)
    : [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
          {server?.totalLabel !== undefined ? <span>{server.totalLabel}</span> : null}
          <span data-testid="datatable-count">{countLabel}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toolbar}
          {activeFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="datatable-reset"
              onClick={resetFilters}
            >
              Filter zurücksetzen
            </Button>
          ) : null}
        </div>
      </div>

      {showServerHint ? (
        <p data-testid="datatable-server-hint" className="text-xs text-muted">
          Filter wirkt auf die geladenen Zeilen.
        </p>
      ) : null}

      <Table caption={caption} {...(className !== undefined ? { className } : {})}>
        <THead>
          <tr>
            {columns.map((col) => (
              <TH
                key={col.id}
                {...(col.headerClassName !== undefined ? { className: col.headerClassName } : {})}
              >
                {col.header}
              </TH>
            ))}
          </tr>
          {showFilterRow ? (
            <tr>
              {columns.map((col) => (
                <th key={col.id} className="px-3 pb-2">
                  {col.filter === false ? null : col.filter === 'select' ? (
                    <SelectFilterCell
                      columnId={col.id}
                      label={filterLabelOf(col)}
                      value={filters[col.id] ?? ''}
                      options={selectOptionsByColumn.get(col.id) ?? []}
                      setFilter={setFilter}
                    />
                  ) : (
                    <TextFilterCell
                      columnId={col.id}
                      label={filterLabelOf(col)}
                      value={filters[col.id] ?? ''}
                      setFilter={setFilter}
                    />
                  )}
                </th>
              ))}
            </tr>
          ) : null}
        </THead>
        <TBody>
          {displayedIndexes.length === 0 ? (
            <TR>
              <TD colSpan={columns.length}>
                <span data-testid="datatable-nomatch">
                  {noMatchContent ?? 'Keine Zeile passt zum Filter.'}
                </span>
              </TD>
            </TR>
          ) : (
            displayedIndexes.map((rowIndex) => {
              const row = rows[rowIndex];
              if (row === undefined) {
                return null;
              }
              const extra = rowExtra ? rowExtra(row) : null;
              return (
                <Fragment key={rowKey(row, rowIndex)}>
                  <TR
                    {...(onRowClick ? { onClick: () => onRowClick(row) } : {})}
                    {...(rowSelected ? { selected: rowSelected(row) } : {})}
                    {...(rowMuted ? { muted: rowMuted(row) } : {})}
                    {...(rowTestId ? { 'data-testid': rowTestId(row) } : {})}
                  >
                    {columns.map((col) => (
                      <TD
                        key={col.id}
                        {...(col.className !== undefined ? { className: col.className } : {})}
                      >
                        {col.cell(row)}
                      </TD>
                    ))}
                  </TR>
                  {extra !== null && extra !== undefined && extra !== false ? (
                    <TR>
                      <TD colSpan={columns.length}>{extra}</TD>
                    </TR>
                  ) : null}
                </Fragment>
              );
            })
          )}
          {footerRows}
        </TBody>
      </Table>

      {server !== undefined ? (
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor={pageSizeId} className="text-sm text-muted">
            Zeilen pro Seite
          </label>
          <select
            id={pageSizeId}
            data-testid="datatable-pagesize"
            className={PAGE_SIZE_SELECT_CLASS}
            value={String(server.pageSize)}
            onChange={(event) => server.onPageSizeChange(Number(event.target.value))}
          >
            {serverPageSizeOptions.map((opt) => (
              <option key={opt} value={String(opt)}>
                {opt}
              </option>
            ))}
          </select>
          {/* Zwei dauerhaft ausgegraute Knöpfe sind schlimmer als keine: manche
              Listen (z.B. GET /runs) kennen gar kein Blättern, dort steuert die
              Seitengröße allein das limit. */}
          {server.hasPrev || server.hasNext ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="datatable-prev"
                disabled={!server.hasPrev}
                onClick={server.onPrev}
              >
                Zurück
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="datatable-next"
                disabled={!server.hasNext}
                onClick={server.onNext}
              >
                Weiter
              </Button>
            </>
          ) : null}
        </div>
      ) : showClientPager ? (
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor={pageSizeId} className="text-sm text-muted">
            Zeilen pro Seite
          </label>
          <select
            id={pageSizeId}
            data-testid="datatable-pagesize"
            className={PAGE_SIZE_SELECT_CLASS}
            value={clientPageSize === 'alle' ? 'alle' : String(clientPageSize)}
            onChange={(event) => {
              const next = event.target.value;
              if (next === 'alle') {
                setClientPageSize('alle');
                return;
              }
              setClientPageSize(Number(next));
            }}
          >
            {clientOptions.map((opt) => (
              <option key={String(opt)} value={opt === 'alle' ? 'alle' : String(opt)}>
                {opt === 'alle' ? 'alle' : String(opt)}
              </option>
            ))}
          </select>
          {clientPageSize === 'alle' ? null : (
            <>
              <span data-testid="datatable-page">
                Seite {safePage + 1} von {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="datatable-prev"
                disabled={safePage === 0}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              >
                Zurück
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="datatable-next"
                disabled={safePage + 1 >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Weiter
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
